<script setup lang="ts">
    import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
    import { temporaryUUID } from '../composables/useTemporaryUUID';

    const model = defineModel<string | number | undefined>();

    const props = withDefaults(
        defineProps<{
            items: {
                value: string | number;
                label: string;
            }[];
            spread?: boolean;
            id: string;
            disabled?: boolean;
            size?: 'small' | 'large';
        }>(),
        {
            size: 'small',
        }
    );

    const uuid = temporaryUUID();
    const hasMounted = ref(false);
    const labelRefs = ref<HTMLLabelElement[]>([]);
    const containerRef = ref<HTMLDivElement>();
    const resizeTick = ref(0);

    let resizeObserver: ResizeObserver | undefined;

    onMounted(() => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                hasMounted.value = true;
            });
        });

        resizeObserver = new ResizeObserver(() => {
            resizeTick.value++;
        });
        if (containerRef.value) {
            resizeObserver.observe(containerRef.value);
        }
    });

    onBeforeUnmount(() => {
        resizeObserver?.disconnect();
    });

    const selectedIndex = computed(() =>
        props.items.findIndex((item) => String(item.value) === String(model.value))
    );

    const indicatorStyle = computed(() => {
        void resizeTick.value;
        const idx = selectedIndex.value;
        if (idx < 0 || !labelRefs.value[idx]) return null;
        const el = labelRefs.value[idx];
        if (!el.offsetWidth) return null;
        return {
            width: `${el.offsetWidth}px`,
            transform: `translateX(${el.offsetLeft}px)`,
        };
    });
</script>

<template>
    <div
        class="inline-flex bg-gray-lightest has-focus-visible:outline-gray-dark has-focus-visible:outline-2 has-focus-visible:outline-offset-2"
        :class="{
            'w-full': spread,
            'opacity-50 pointer-events-none': disabled,
            'p-0.75 border border-gray-light rounded-sm': size === 'small',
            'p-1.5 rounded-sm': size === 'large',
        }"
    >
        <div ref="containerRef" class="relative flex w-full">
            <div
                v-if="indicatorStyle"
                class="absolute inset-y-0 bg-primary-avocado text-white rounded-xs"
                :class="{
                    'transition-[transform,width] duration-200 ease-in-out': hasMounted,
                }"
                :style="indicatorStyle"
            />
            <label
                v-for="item in items"
                :key="item.value"
                ref="labelRefs"
                :for="id + '_' + item.value + '_' + uuid"
                class="relative z-10 cursor-pointer rounded-xs shrink-0 text-center block"
                :class="{
                    'text-primary-lime': String(item.value) === String(model),
                    'transition-colors duration-200': hasMounted,
                    'flex-1': spread,
                    'px-3 py-0.5 text-xs': size === 'small',
                    'px-6 py-1.5 text-base': size === 'large',
                }"
            >
                <input
                    :id="id + '_' + item.value + '_' + uuid"
                    v-model="model"
                    type="radio"
                    :name="id + '_' + uuid"
                    :value="item.value"
                    :aria-label="item.label"
                    class="absolute -top-full opacity-0"
                    :disabled="disabled"
                />
                <span>{{ item.label }}</span>
            </label>
        </div>
    </div>
</template>
